import { useEffect, useState, useRef } from "react";
import { EventSource, type ErrorEvent } from "eventsource";

export type SseErrorEvent = ErrorEvent;

export type SseStatus = "connecting" | "connected" | "disconnected";

interface UseSSEOptions {
  url: string;
  sessionId: string;
  enabled: boolean; // 이 값이 true일 때만 연결
  messageHandlers?: Record<string, (event: MessageEvent) => void>; // 이벤트명별 추가 핸들러
  onError?: (error: SseErrorEvent) => void;
  onOpen?: (event: Event) => void;
}

export function useSSE({ url, sessionId, enabled, messageHandlers, onError, onOpen }: UseSSEOptions) {
  const messageHandlersRef = useRef(messageHandlers);
  const onErrorRef = useRef(onError);
  const onOpenRef = useRef(onOpen);

  useEffect(() => {
    messageHandlersRef.current = messageHandlers;
    onErrorRef.current = onError;
    onOpenRef.current = onOpen;
  });

  const [status, setStatus] = useState<SseStatus>("disconnected");
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!enabled) {
      return; // 비활성 상태면 아예 연결하지 않음
    }

    let destroyed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    const clearReconnectTimer = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };
    let lastActivity = Date.now();

    const connect = () => {
      if (destroyed) return; // 정리된 이후엔 새로 연결하지 않음
      if (eventSourceRef.current) return; // 중복 연결 방지

      lastActivity = Date.now();

      const eventSource = new EventSource(url, {
        fetch: (input, init) => {
          const headers = new Headers(init?.headers);
          headers.set("x-session-id", sessionId);
          return fetch(input, { ...init, headers });
        },
      });
      eventSourceRef.current = eventSource;
      const isCurrent = () => eventSourceRef.current === eventSource;
      setStatus("connecting");

      eventSource.addEventListener("open", (event) => {
        if (!isCurrent()) return;

        onOpenRef.current?.(event);
        setStatus("connected");
      });

      eventSource.addEventListener("error", (error) => {
        if (!isCurrent()) return;

        if (eventSource.readyState === EventSource.CLOSED) {
          eventSourceRef.current = null;
          setStatus("disconnected");

          // 재연결
          clearReconnectTimer();
          reconnectTimer = setTimeout(() => {
            if (!destroyed) connect();
          }, 3000);
        } else {
          setStatus("connecting");
        }
        onErrorRef.current?.(error);
      });

      eventSource.addEventListener("heartbeat", (_event) => {
        if (!isCurrent()) return;
        lastActivity = Date.now();
      });

      for (const type of Object.keys(messageHandlersRef.current ?? {})) {
        eventSource.addEventListener(type, (ev) => {
          if (!isCurrent()) return;
          messageHandlersRef.current?.[type]?.(ev);
        });
      }
    };

    connect();

    const idleCheckTimer = setInterval(() => {
      if (!eventSourceRef.current) return;
      if (Date.now() - lastActivity > 11000) { // 11초 이상 무응답
        eventSourceRef.current.close();
        eventSourceRef.current = null;
        clearReconnectTimer();
        connect();
      }
    }, 100);

    // enabled가 false로 바뀌거나 언마운트되면 연결 정리
    return () => {
      destroyed = true;
      setStatus("disconnected");
      clearReconnectTimer(); // 대기 중인 재연결 타이머 정리
      clearInterval(idleCheckTimer);

      const eventSource = eventSourceRef.current;
      eventSourceRef.current = null;

      // async로 처리하지 않으면, devtools에서 exception 발생함
      (async () => {
        try {
          eventSource?.close();
        } catch(e) {
          console.log(e);
        }
      })();
    };
  }, [url, sessionId, enabled]); // url, sessionId, enabled 이 바뀔 때만 재연결

  return { status };
}
